import { useState } from 'react';
import { Button } from './ui.tsx';
import { t } from '../i18n/index.ts';
import { cellText, outLabel, type OutCol, type Result } from '../lib/reports.ts';

const key = (v: unknown) => (v === null || v === undefined ? '' : String(v));

/**
 * A report grouped by two things, one down and one across, like a spreadsheet's pivot table. The
 * margins come from the database, so an average or a maximum is right, not a sum of the cells.
 * The two groups can be swapped, and with several things added up, one is shown at a time.
 */
export function PivotTable({ result, compact }: { result: Result; compact?: boolean }) {
  const [swapped, setSwapped] = useState(false);
  const measures = result.columns.map((c, i) => ({ c, i })).filter((x) => x.c.agg);
  const [m, setM] = useState(0);
  const measure = measures[Math.min(m, measures.length - 1)];
  if (!measure || result.columns.length < 3) return null;
  const [down, across] = swapped ? [1, 0] : [0, 1];
  const a = result.columns[down] as OutCol;
  const b = result.columns[across] as OutCol;
  const mc = measure.c;
  const rowsK = [...new Set(result.rows.map((r) => key(r[down])))];
  const colsK = [...new Set(result.rows.map((r) => key(r[across])))].sort();
  const cell = new Map(result.rows.map((r) => [`${key(r[down])}|${key(r[across])}`, r[measure.i]]));
  // The measure's place among the measures, in the totals the server sent.
  const at = measures.indexOf(measure);
  const totals = result.totals;
  const margin = (list: unknown[][] | undefined, k: string) => list?.find((x) => key(x[0]) === k)?.[at + 1];
  const rowTotal = (k: string) => margin(swapped ? totals?.cols : totals?.rows, k);
  const colTotal = (k: string) => margin(swapped ? totals?.rows : totals?.cols, k);
  const grand = totals?.grand[at];
  return (
    <div className="pivot">
      {!compact ? (
        <div className="pivot-tools">
          {measures.length > 1 ? (
            <div className="segmented" role="radiogroup" aria-label={t('rpt.pivot_measure')}>
              {measures.map((x, n) => (
                <button key={x.i} type="button" role="radio" aria-checked={x === measure} className={x === measure ? 'active' : ''} onClick={() => setM(n)}>
                  {outLabel(x.c)}
                </button>
              ))}
            </div>
          ) : (
            <span className="muted small">{outLabel(mc)}</span>
          )}
          <Button size="sm" variant="ghost" icon="repeat" onClick={() => setSwapped(!swapped)}>
            {t('rpt.pivot_swap')}
          </Button>
        </div>
      ) : null}
      <div className="table-wrap dash-pivot" role="region" aria-label={outLabel(mc)} tabIndex={0}>
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col" className="left">
                {outLabel(a)} \ {outLabel(b)}
              </th>
              {colsK.map((ck) => (
                <th key={ck} scope="col">
                  {cellText(b, ck || null)}
                </th>
              ))}
              {totals ? <th scope="col">{t('rpt.total')}</th> : null}
            </tr>
          </thead>
          <tbody>
            {rowsK.map((rk) => (
              <tr key={rk}>
                <th scope="row" className="left">
                  {cellText(a, rk || null)}
                </th>
                {colsK.map((ck) => (
                  <td key={ck} className="tabular">
                    {cell.has(`${rk}|${ck}`) ? cellText(mc, cell.get(`${rk}|${ck}`)) : '—'}
                  </td>
                ))}
                {totals ? <td className="tabular strong">{cellText(mc, rowTotal(rk) ?? null)}</td> : null}
              </tr>
            ))}
          </tbody>
          {totals ? (
            <tfoot>
              <tr>
                <td className="left">{t('rpt.total')}</td>
                {colsK.map((ck) => (
                  <td key={ck} className="tabular">
                    {cellText(mc, colTotal(ck) ?? null)}
                  </td>
                ))}
                <td className="tabular">{cellText(mc, grand ?? null)}</td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
}
